import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  DollarSign,
  LucideIcon,
  Menu,
  User,
} from "lucide-react";
import { useState } from "react";
import { useConnectDispatch, useConnectState } from "../ConnectContext";
import ConnectStep from "../components/ConnectStep";
import { InstitutionHeader } from "../components/InstitutionHeader";
import { ActionType } from "../types";

const lowercaseFirst = (str: string) =>
  str.charAt(0).toLowerCase() + str.slice(1);

const buildPermissionText = (
  appName: string,
  balancesEnabled: boolean,
  transactionsEnabled: boolean,
): string => {
  const restrictions: string[] = [];

  if (!balancesEnabled) {
    restrictions.push("ver tus balances");
  }
  if (!transactionsEnabled) {
    restrictions.push("consultar tu historial de transacciones");
  }

  if (restrictions.length === 0) {
    return `${appName} no podrá realizar movimientos en tu cuenta. Puedes revocar el acceso a tus datos en cualquier momento.`;
  }

  const restrictionText = restrictions.join(", ");
  return `${appName} no podrá ${restrictionText} ni realizar movimientos en tu cuenta. Puedes revocar el acceso a tus datos en cualquier momento.`;
};

const PermissionCard = ({
  icon: Icon,
  title,
  appName,
  reason,
  items,
  index,
}: {
  icon: LucideIcon;
  title: string;
  appName: string;
  reason?: string;
  items: string[];
  index: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: 0.1 + index * 0.08,
        ease: [0.25, 1, 0.5, 1],
      }}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="rounded-xl border border-border/70 bg-card overflow-hidden transition-shadow duration-200 hover:shadow-sm">
          <CollapsibleTrigger className="w-full px-3 py-2.5 text-left">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-bank-surface flex items-center justify-center">
                  <Icon className="h-[18px] w-[18px] text-bank-primary" />
                </div>
                <span className="font-medium text-foreground text-[15px]">
                  {title}
                </span>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="CollapsibleContent">
            <div className="px-4 pb-4 pt-0">
              <div className="border-t border-border/50 pt-3 space-y-3">
                {/* Requested data */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Datos solicitados
                  </p>
                  <ul className="space-y-1.5">
                    {items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-2 text-sm text-foreground"
                      >
                        <Check className="w-4 h-4 text-bank-primary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Usage reason */}
                {reason && (
                  <div className="pt-2 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      Cómo son utilizados
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {appName}
                      </span>{" "}
                      indicó que utiliza los datos de {title.toLowerCase()} para{" "}
                      <span className="font-medium text-foreground">
                        {lowercaseFirst(reason)}
                      </span>
                      .
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </motion.div>
  );
};

const PermissionsStep = () => {
  const { institution, appConfig } = useConnectState();
  const dispatch = useConnectDispatch();

  if (!appConfig) {
    return null;
  }

  const handleNext = () => {
    dispatch({
      type: ActionType.AcceptPermissions,
    });
  };

  const balancesEnabled = appConfig.permissions?.balances?.enabled ?? true;
  const transactionsEnabled =
    appConfig.permissions?.transactions?.enabled ?? true;
  const balancesReason = appConfig.permissions?.balances?.reason;
  const transactionsReason = appConfig.permissions?.transactions?.reason;

  return (
    <ConnectStep
      stepId="permissions"
      nextButtonLabel="Continuar"
      onClickNext={handleNext}
      persistentScroll
    >
      {/* Bank icon */}
      <div className="mb-3">
        <InstitutionHeader colors={institution} />
      </div>

      {/* Title */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="text-lg leading-relaxed mb-4"
      >
        <span className="font-semibold text-foreground">{appConfig.name}</span>{" "}
        <span className="text-muted-foreground">
          quiere acceder a estos datos financieros en
        </span>{" "}
        <span className="font-semibold text-bank-primary">
          {institution?.name}
        </span>
      </motion.p>

      {/* Permission cards */}
      <div className="space-y-2">
        <PermissionCard
          icon={User}
          title="Información de cuentas"
          appName={appConfig.name}
          items={["Nombre de la cuenta", "Tipo de cuenta", "Últimos 4 dígitos"]}
          index={0}
        />

        {balancesEnabled && (
          <PermissionCard
            icon={DollarSign}
            title="Balances"
            appName={appConfig.name}
            items={["Balance actual", "Balance disponible", "Limites"]}
            reason={balancesReason}
            index={1}
          />
        )}

        {transactionsEnabled && (
          <PermissionCard
            icon={Menu}
            title="Transacciones"
            appName={appConfig.name}
            items={["Historial de transacciones", "Transacciones futuras"]}
            reason={transactionsReason}
            index={2}
          />
        )}
      </div>

      {/* Disclaimer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-sm text-muted-foreground mt-4 leading-relaxed"
      >
        {buildPermissionText(
          appConfig.name,
          balancesEnabled,
          transactionsEnabled,
        )}
      </motion.p>
    </ConnectStep>
  );
};

export default PermissionsStep;
